import { getLocaleInfo } from '../../lang/locales'
import type { MapboxRetrieveResult, MapboxSearchSuggestion } from './types'

interface SuggestResponse {
  suggestions: Array<{
    name: string
    place_formatted: string
    mapbox_id: string
    distance?: number
    poiCategories?: Array<{ id: string; name: string }>
    countryCode?: string
  }>
}

export class MapboxSearchAPI {
  langCode = 'en'
  sessionToken: string
  userCoordinates: UserCoordinates
  distanceUnit?: string

  constructor(
    langCode: string,
    sessionId: string,
    userCoordinates?: UserCoordinates,
  ) {
    if (!sessionId) {
      throw new Error(
        'MapboxSearchAPI: sessionId param missing from constructor',
      )
    }
    this.langCode = langCode
    this.sessionToken = sessionId
    this.distanceUnit = getLocaleInfo(langCode)?.distanceUnit
    if (userCoordinates && userCoordinates.lat && userCoordinates.lng) {
      this.userCoordinates = userCoordinates
    }
  }

  async get(
    input: string,
    options?: { geocodeMode?: boolean },
  ): Promise<MapboxSearchSuggestion[]> {
    const url = this.getUrl(input, options)
    const response = await $fetch<SuggestResponse>(url, {
      timeout: 10000,
    })
    return this.parseResponse(response)
  }

  private getUrl(query: string, options?: { geocodeMode?: boolean }): string {
    if (!query) {
      console.error('MapboxSearchAPI called with empty query')
    }

    const params = new URLSearchParams({
      q: query,
      session_token: this.sessionToken,
      language: this.langCode,
    })

    if (this.userCoordinates) {
      params.set('lng', String(this.userCoordinates.lng))
      params.set('lat', String(this.userCoordinates.lat))
    }

    if (options?.geocodeMode) {
      params.set('geocode_mode', '1')
    }

    return `/api/mb/suggest/?${params.toString()}`
  }

  private parseResponse(body: SuggestResponse): MapboxSearchSuggestion[] {
    if (!body.suggestions) return []
    return body.suggestions.map((suggestion) => this.mapRow(suggestion))
  }

  private mapRow(
    suggestion: SuggestResponse['suggestions'][number],
  ): MapboxSearchSuggestion {
    return {
      name: suggestion.name,
      placeFormatted: suggestion.place_formatted,
      mapboxId: suggestion.mapbox_id,
      distance: suggestion.distance
        ? this.metersToUserUnit(suggestion.distance)
        : undefined,
      poiCategories: suggestion.poiCategories ?? [],
      countryCode: suggestion.countryCode ?? '',
    }
  }

  private metersToUserUnit(meters: number): number {
    if (this.distanceUnit === 'km') {
      return metersToKM(meters)
    }
    return metersToMiles(meters)
  }

  async getPointForSuggestion(mapboxId: string): Promise<MapboxRetrieveResult> {
    const params = new URLSearchParams({
      session_token: this.sessionToken,
    })
    return await $fetch<MapboxRetrieveResult>(
      `/api/mb/retrieve/${mapboxId}?${params.toString()}`,
      {
        timeout: 10000,
      },
    )
  }
}

function metersToMiles(meters: number): number {
  const meterToMileRatio = 1609.344
  return Math.round(meters / meterToMileRatio)
}

function metersToKM(meters: number): number {
  return Math.round(meters / 1000)
}

interface UserCoordinates {
  lat: number
  lng: number
}
